import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const DrillDownModal = ({ isOpen, onClose, data, title, type }) => {
  if (!data) return null;

  const renderContent = () => {
    switch (type) {
      case 'revenue_daily':
        return (
          <div className="space-y-3">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 font-semibold">Date</th>
                    <th className="text-right py-3 px-4 font-semibold">Revenue</th>
                    <th className="text-right py-3 px-4 font-semibold">Transactions</th>
                    <th className="text-right py-3 px-4 font-semibold">Avg/Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={`${item.date}-${item.institution_id || item.id || item.name || ''}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">{new Date(item.date).toLocaleDateString('en-IN')}</td>
                      <td className="py-3 px-4 text-right font-medium text-green-600">
                        ₹{item.revenue?.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </td>
                      <td className="py-3 px-4 text-right">{item.transactions}</td>
                      <td className="py-3 px-4 text-right text-slate-600">
                        ₹{(item.revenue / item.transactions).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-slate-300 bg-slate-50">
                  <tr>
                    <td className="py-3 px-4 font-semibold">Total</td>
                    <td className="py-3 px-4 text-right font-semibold text-green-600">
                      ₹{data.reduce((sum, item) => sum + item.revenue, 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {data.reduce((sum, item) => sum + item.transactions, 0)}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-600">
                      ₹{(data.reduce((sum, item) => sum + item.revenue, 0) / data.reduce((sum, item) => sum + item.transactions, 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      
      case 'users_growth':
        return (
          <div className="space-y-3">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 font-semibold">Date</th>
                    <th className="text-right py-3 px-4 font-semibold">New Users</th>
                    <th className="text-right py-3 px-4 font-semibold">Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const cumulative = data.slice(0, index + 1).reduce((sum, d) => sum + d.new_users, 0);
                    return (
                      <tr key={`${item.date}-${item.institution_id || item.id || item.name || ''}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">{new Date(item.date).toLocaleDateString('en-IN')}</td>
                        <td className="py-3 px-4 text-right font-medium text-blue-600">{item.new_users}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{cumulative}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'patients_growth':
        return (
          <div className="space-y-3">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 font-semibold">Date</th>
                    <th className="text-right py-3 px-4 font-semibold">New Patients</th>
                    <th className="text-right py-3 px-4 font-semibold">Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const cumulative = data.slice(0, index + 1).reduce((sum, d) => sum + d.new_patients, 0);
                    return (
                      <tr key={`${item.date}-${item.institution_id || item.id || item.name || ''}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">{new Date(item.date).toLocaleDateString('en-IN')}</td>
                        <td className="py-3 px-4 text-right font-medium text-teal-600">{item.new_patients}</td>
                        <td className="py-3 px-4 text-right text-slate-600">{cumulative}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      default:
        return <div className="text-center text-slate-400 py-8">No detailed data available</div>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center justify-between">
            {title}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Detailed breakdown and analysis
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownModal;
